import { useMemo } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, X } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import type { CampaignImportRowError } from "@/types/campaign";

interface Props {
  open: boolean;
  /** `detail` from the service — e.g. "Import failed with 5 error(s)". */
  detail?: string | null;
  errors: CampaignImportRowError[];
  onClose: () => void;
}

/** Row number → its failures, in the order the service reported them. */
function groupByRow(errors: CampaignImportRowError[]): [number, CampaignImportRowError[]][] {
  const byRow = new Map<number, CampaignImportRowError[]>();
  for (const error of errors) {
    const row = byRow.get(error.row_number);
    if (row) row.push(error);
    else byRow.set(error.row_number, [error]);
  }
  return [...byRow.entries()].sort((a, b) => a[0] - b[0]);
}

/**
 * The per-row failures from a rejected CSV import. A toast cannot carry a list
 * this long, and the user needs the line numbers to fix the file — so the
 * import errors get a modal of their own and the drawer stays open behind it.
 */
export function CampaignImportErrorsModal({ open, detail, errors, onClose }: Props) {
  const rows = useMemo(() => groupByRow(errors), [errors]);

  if (!open || errors.length === 0) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/45"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="campaign-import-errors-title"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden ring-1 ring-red-500/10"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-red-500/20 bg-red-50">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-red-100 text-red-600">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 id="campaign-import-errors-title" className="text-base font-bold text-slate-900">
                CSV import failed
              </h2>
              <p className="text-xs text-red-700 mt-0.5">
                {detail?.trim() ||
                  `Import failed with ${errors.length} error${errors.length === 1 ? "" : "s"}.`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-slate-500 hover:bg-red-100 hover:text-red-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
          <p className="text-xs text-bridge-muted mb-3">
            Fix these rows in the file and upload it again. Row numbers match the lines in your
            CSV — data starts at line 3.
          </p>
          <ul className="space-y-3">
            {rows.map(([rowNumber, rowErrors]) => (
              <li
                key={rowNumber}
                className="rounded-xl border border-bridge-border/70 overflow-hidden"
              >
                <div className="px-3 py-2 bg-slate-50 border-b border-bridge-border/60 text-xs font-bold text-slate-700">
                  Row {rowNumber}
                  <span className="ml-2 font-medium text-bridge-muted">
                    {rowErrors.length} error{rowErrors.length === 1 ? "" : "s"}
                  </span>
                </div>
                <ul className="divide-y divide-bridge-border/50">
                  {rowErrors.map((error, index) => (
                    <li
                      key={`${error.field}-${error.code}-${index}`}
                      className="px-3 py-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm"
                    >
                      <span className="font-semibold text-slate-800">{error.field}</span>
                      <span className="text-[11px] font-mono uppercase tracking-wide text-red-600 bg-red-50 rounded px-1.5 py-0.5">
                        {error.code}
                      </span>
                      <span className="text-bridge-muted">{error.message}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>

        <div className="px-5 py-4 border-t border-bridge-border/60 bg-gray-50 flex justify-end">
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
